<script setup lang="ts">
import { computed, unref } from 'vue';
import { storeToRefs } from 'pinia';
import { useHomeHeaderCustomisation } from '../composables/useHomeHeaderCustomisation';
import { useGetPageField, useIsSectionVisible } from '@/common/customisation/composables';
import { getImageFieldUrl } from '@/common/customisation/utils/imageFieldValue';
import { PUI } from '@pleased-npm/fe-customisation';
import MinimizeIcon from '@/components/icons/MinimizeIcon.vue';
import { Bridge, platform } from '@/common/bridge';
import { useWidgetConversation } from '@/stores/useWidgetConversation';

const widgetConversation = useWidgetConversation();
const { aiFlow, supportWizard } = storeToRefs(widgetConversation);

const isAiFlowActive = computed(() => Boolean(unref(aiFlow.value.active)));
const isLiveChatActive = computed(() => Boolean(unref(supportWizard.value.support.chat.active)));
const isVoiceCallActive = computed(() => Boolean(unref(supportWizard.value.support.voice.voiceChatId)));

const handleMinimizeClick = () => {
  const canCloseParentOnWap = !isAiFlowActive.value && !isLiveChatActive.value && !isVoiceCallActive.value;

  if (platform === 'wap' && canCloseParentOnWap) {
    Bridge.sendToParent('CLOSE');
    return;
  }

  Bridge.minimize();
};

const customisation = useHomeHeaderCustomisation();

const logoField = useGetPageField({
  registryId: PUI.REGISTRY_IDS.DEFAULT_THEME,
  blockKey: PUI.THEME_BLOCK_KEYS.BRAND_ASSETS,
  sectionKey: PUI.THEME_SECTION_KEYS.BRAND_LOGO,
  fieldKey: PUI.THEME_FIELD_KEYS.LOGO_IMAGE,
});

const logoUrl = computed(() => getImageFieldUrl(logoField.value));

const isLogoVisible = useIsSectionVisible({
  registryId: PUI.REGISTRY_IDS.HOME,
  sectionKey: PUI.HOME_SECTION_KEYS.BRAND_LOGO,
});

const backgroundStyle = computed(() => {
  if (!customisation.isVisible.value) {
    return {};
  }

  const { backgroundType, backgroundColor, backgroundImage } = customisation;

  if (backgroundType.value === 'image' && backgroundImage.value) {
    const bgColor = backgroundColor.value;
    return {
      backgroundImage: `url(${backgroundImage.value})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      ...(bgColor ? { backgroundColor: bgColor } : {}),
    };
  }

  const bgColor = backgroundColor.value;
  return bgColor ? { backgroundColor: bgColor } : {};
});
</script>

<template>
  <div class="home-header" :style="backgroundStyle">
    <div class="header-top">
      <div v-if="!(logoUrl && isLogoVisible)" class="header-top__spacer" aria-hidden="true" />
      <img v-if="logoUrl && isLogoVisible" :src="logoUrl" alt="Logo" class="header-logo" />
      <button class="close-button" aria-label="Minimize" @click="handleMinimizeClick">
        <MinimizeIcon />
      </button>
    </div>
    <slot />
  </div>
</template>

<style scoped lang="scss">
.home-header {
  padding: 24px;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 4px;
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-top__spacer {
  width: 0;
  min-width: 0;
}

.header-logo {
  max-height: 40px;
  max-width: 150px;
  object-fit: contain;
}

.close-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  // TODO: move to customisation
  color: white;

  svg {
    width: 24px;
    height: 24px;
  }
}
</style>
